import { useCallback, useContext } from "react";
import { useParams } from "react-router-dom";

import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { ChannelList, fetchChannelList } from "@/models/repositories";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider.tsx";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";

export default function FirstLocationRedirectHook() {
  const { currentWorkspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const { subPage } = useParams();
  const { myAccount } = useContext(MyAccountContext);
  const list = useReactiveSelectQuery(
    fetchChannelList,
    [
      {
        channelOrder: "driver-activity",
        channelType: "all-channels",
        workspaceId: currentWorkspaceId,
        myCurrentWorkspaceMembership,
        searchValue: "",
        myAccount,
        channelFilters: [],
        selectedFeedGroups: [],
      },
    ],
    "workspaceId.fetchChannelList",
    300,
  ) as ChannelList[] | null | undefined;

  /** Wait until the reactive select has a resolved row set (not the initial unrun state). */
  const isChannelListLoading = list == null;
  /** Query returned no channels — local DB may be stale. */
  const isChannelListEmpty = list != null && Array.isArray(list) && list.length === 0;

  const redirectUrl = useCallback(
    (base = "drivers"): string => {
      if (list?.length > 0) {
        const redirectType = base ? base : "channels";

        const filteredByType =
          base !== null
            ? base === "drivers"
              ? list.filter((i) => i.aliasChannel)
              : list.filter((i) => !i.aliasChannel)
            : list;

        const firstActiveChannel = filteredByType.find(
          (channel) => channel?.id && channel?.workspaceId && channel?.enabled && channel?.name === "read",
        );
        if (firstActiveChannel?.workspaceId && firstActiveChannel?.id && redirectType) {
          if (redirectType === "drivers" && firstActiveChannel.aliasMembershipId) {
            return `/workspaces/${firstActiveChannel.workspaceId}/drivers/${firstActiveChannel.aliasMembershipId}/channel/${firstActiveChannel.id}`;
          }
          return `/workspaces/${firstActiveChannel.workspaceId}/${redirectType}/${firstActiveChannel.id}`;
        }
      }
    },
    [list, subPage],
  );

  return {
    list,
    isChannelListLoading,
    isChannelListEmpty,
    redirectUrl,
  };
}
